import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, CircularProgress } from "@mui/material";
import axios from "axios";
import CommonButton from "../../components/CommonButton/CommonButton";
import Loader from "../../components/loader/Loader";
import {
  GET_BUSINESS_VIDEOS,
  ADD_DEFAULT_VIDEO,
  ADD_VIDEO,
  ACTIVATE_VIDEO,
  DEACTIVATE_VIDEO
} from "../../utils/url";
import RefreshIcon from "@mui/icons-material/Refresh";
import DefaultVideoDialog from "./components/DefaultVideoDialog";
import UploadVideoDialog from "./components/UploadVideoDialog";
import ActivateVideoDialog from "./components/ActivateVideoDialog";
import DeactivateVideoDialog from "./components/DeactivateVideoDialog";
import RefreshDialog from "./components/RefreshDialog";
import VideoTable from "./components/VideoTable";
import { Video } from "./components/types";
import UploadFile from "../../utils/uploadFile";

const BusinessDeviceVideoPage = () => {
  const params = useParams();
  const businessId = params.businessId as string;
  const [videos, setVideos] = useState<Video[]>([]);
  const [openDefaultVideoDialog, setOpenDefaultVideoDialog] = useState(false);
  const [selectedDeviceType, setSelectedDeviceType] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<
    "idle" | "fetching" | "success" | "error"
  >("idle");
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [openActivateModal, setOpenActivateModal] = useState(false);
  const [openDeactivateModal, setOpenDeactivateModal] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string>("");
  const [activateError, setActivateError] = useState<string>("");
  const [deactivateError, setDeactivateError] = useState<string>("");
  const [activateLoading, setActivateLoading] = useState(false);
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  const [defaultVideoLoading, setDefaultVideoLoading] = useState(false);
  const [defaultVideoError, setDefaultVideoError] = useState<string>("");
  const [refreshing, setRefreshing] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedAppType, setSelectedAppType] =
    useState<string>("PaymentAppRewardle");

  const fetchVideos = async () => {
    try {
      setStatus("fetching");
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}${GET_BUSINESS_VIDEOS(
          businessId
        )}`,
        {
          headers: {
            Authorization: `bearer ${localStorage.getItem("accessToken")}`
          }
        }
      );
      const videosData = Array.isArray(response.data) ? response.data : [];
      setVideos(videosData);
      setStatus("success");
    } catch (error) {
      console.error("Error fetching videos:", error);
      setStatus("error");
      setVideos([]);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, [businessId]);

  const handleAddDefaultVideo = async () => {
    try {
      setDefaultVideoError("");
      setDefaultVideoLoading(true);
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}${ADD_DEFAULT_VIDEO}`,
        {
          videoId: selectedDeviceType,
          businessId: businessId
        },
        {
          headers: {
            Authorization: `bearer ${localStorage.getItem("accessToken")}`
          }
        }
      );
      setOpenDefaultVideoDialog(false);
      fetchVideos();
    } catch (error: any) {
      console.error("Error adding default video:", error);
      const errorMessage =
        error.response?.data?.errors?.[0]?.Message ||
        "Failed to add default video. Please try again.";
      setDefaultVideoError(errorMessage);
    } finally {
      setDefaultVideoLoading(false);
    }
  };

  const handleDownload = async (videoURL: string) => {
    if (!videoURL) {
      console.error("No video URL provided");
      return;
    }

    try {
      const response = await axios.get(videoURL, {
        responseType: "blob",
        headers: {
          Authorization: `bearer ${localStorage.getItem("accessToken")}`
        }
      });

      const blob = new Blob([response.data], { type: "video/mp4" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `video-${Date.now()}.mp4`);
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading video:", error);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadError("");
    }
  };

  const handleUploadVideo = async () => {
    if (!selectedFile) {
      setUploadError("Please select a video file");
      return;
    }

    if (!selectedAppType) {
      setUploadError("Please select an app type");
      return;
    }

    setLoading(true);
    try {
      // Generate a unique filename for the video
      const timestamp = Date.now();
      const videoFileName = `${businessId}_video_${timestamp}.mp4`;
      const s3BucketName: any = process.env.REACT_APP_BUCKET_NAME;

      // Upload the video file to S3
      const videoUrl = await UploadFile(
        selectedFile,
        s3BucketName,
        `business/${businessId}/videos/${videoFileName}`,
        localStorage.getItem("accessToken") || ""
      );

      if (!videoUrl) {
        throw new Error("Failed to Upload video, Please try again!");
      }

      // Only proceed with creating video record if file upload was successful
      try {
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}${ADD_VIDEO}`,
          {
            videoId: timestamp.toString(), // Using timestamp as videoId
            videoURL: videoUrl,
            businessId: businessId,
            appId: selectedAppType
          },
          {
            headers: {
              Authorization: `bearer ${localStorage.getItem("accessToken")}`
            }
          }
        );

        setOpenUploadModal(false);
        fetchVideos();
      } catch (error: any) {
        // If video record creation fails, we should clean up the uploaded file
        // TODO: Add cleanup logic for the uploaded file if needed
        throw new Error(
          error.response?.data?.errors?.[0]?.Message ||
            "Failed to create video record. Please try again."
        );
      }
    } catch (error: any) {
      console.error("Error in video upload process:", error);
      const errorMessage =
        error.message ||
        error.response?.data?.errors?.[0]?.Message ||
        "Failed to upload video. Please try again.";
      setUploadError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleActivateConfirm = async () => {
    try {
      setActivateError("");
      setActivateLoading(true);
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}${ACTIVATE_VIDEO(
          selectedVideoId
        )}`,
        {},
        {
          headers: {
            Authorization: `bearer ${localStorage.getItem("accessToken")}`
          }
        }
      );
      setOpenActivateModal(false);
      fetchVideos();
    } catch (error: any) {
      console.error("Error activating video:", error);
      const errorMessage =
        error.response?.data?.errors?.[0]?.Message ||
        "Failed to activate video. Please try again.";
      setActivateError(errorMessage);
    } finally {
      setActivateLoading(false);
    }
  };

  const handleDeactivateConfirm = async () => {
    try {
      setDeactivateError("");
      setDeactivateLoading(true);
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}${DEACTIVATE_VIDEO(
          selectedVideoId
        )}`,
        {},
        {
          headers: {
            Authorization: `bearer ${localStorage.getItem("accessToken")}`
          }
        }
      );
      setOpenDeactivateModal(false);
      fetchVideos();
    } catch (error: any) {
      console.error("Error deactivating video:", error);
      const errorMessage =
        error.response?.data?.errors?.[0]?.Message ||
        "Failed to deactivate video. Please try again.";
      setDeactivateError(errorMessage);
    } finally {
      setDeactivateLoading(false);
    }
  };

  const handleCloseUploadDialog = () => {
    if (!loading) {
      setOpenUploadModal(false);
      setSelectedFile(null);
      setSelectedAppType("PaymentAppRewardle");
      setUploadError("");
    }
  };

  const handleCloseDefaultVideoDialog = () => {
    if (!defaultVideoLoading) {
      setOpenDefaultVideoDialog(false);
      setSelectedDeviceType("");
      setDefaultVideoError("");
    }
  };

  const handleCloseActivateDialog = () => {
    if (!activateLoading) {
      setOpenActivateModal(false);
      setActivateError("");
    }
  };

  const handleCloseDeactivateDialog = () => {
    if (!deactivateLoading) {
      setOpenDeactivateModal(false);
      setDeactivateError("");
    }
  };

  const handleCloseRefreshDialog = () => {
    if (!refreshing) {
      setOpen(false);
    }
  };

  const handleRefreshData = async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchVideos()]);
    } catch (error) {
      console.error("Error refreshing data:", error);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, gap: 2 }}>
        <CommonButton onClick={() => setOpenUploadModal(true)}>
          Add Video
        </CommonButton>
        <CommonButton onClick={() => setOpenDefaultVideoDialog(true)}>
          Add Default Video
        </CommonButton>
        <Box sx={{ cursor: "pointer" }}>
          {refreshing ? (
            <CircularProgress size={25} color="inherit" />
          ) : (
            <RefreshIcon onClick={handleRefreshData} />
          )}
        </Box>
      </Box>

      {status === "fetching" ? (
        <Loader />
      ) : status === "error" ? (
        <Typography>No Data Available</Typography>
      ) : (
        <VideoTable
          videos={videos}
          status={status}
          onActivate={(videoId) => {
            setSelectedVideoId(videoId);
            setOpenActivateModal(true);
          }}
          onDeactivate={(videoId) => {
            setSelectedVideoId(videoId);
            setOpenDeactivateModal(true);
          }}
          onDownload={handleDownload}
        />
      )}

      <DefaultVideoDialog
        open={openDefaultVideoDialog}
        onClose={handleCloseDefaultVideoDialog}
        onConfirm={handleAddDefaultVideo}
        loading={defaultVideoLoading}
        error={defaultVideoError}
        selectedDeviceType={selectedDeviceType}
        onDeviceTypeChange={setSelectedDeviceType}
      />

      <UploadVideoDialog
        open={openUploadModal}
        onClose={handleCloseUploadDialog}
        onConfirm={handleUploadVideo}
        loading={loading}
        error={uploadError}
        selectedFile={selectedFile}
        onFileSelect={handleFileSelect}
        selectedAppType={selectedAppType}
        onAppTypeChange={setSelectedAppType}
      />

      <ActivateVideoDialog
        open={openActivateModal}
        onClose={handleCloseActivateDialog}
        onConfirm={handleActivateConfirm}
        loading={activateLoading}
        error={activateError}
      />

      <DeactivateVideoDialog
        open={openDeactivateModal}
        onClose={handleCloseDeactivateDialog}
        onConfirm={handleDeactivateConfirm}
        loading={deactivateLoading}
        error={deactivateError}
      />

      <RefreshDialog
        open={open}
        onClose={handleCloseRefreshDialog}
        onConfirm={() => {
          setOpen(false);
        }}
        loading={refreshing}
      />
    </div>
  );
};

export default BusinessDeviceVideoPage;
